import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { HandleRoomChangeProps, RoomSelection } from './types';

export const HotelSearchRoomInputWidgetItem = ({
  room,
  roomIndex,
  onRoomChange,
  onRemove,
  canRemove,
  autoFocus,
}: {
  room: RoomSelection;
  roomIndex: number;
  onRoomChange: (props: HandleRoomChangeProps) => void;
  onRemove: () => void;
  canRemove: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation();
  const id = useId();
  const [adults, setAdults] = useState(() => room.paxes.filter((age) => age >= 18).map(String));
  const [children, setChildren] = useState(() => room.paxes.filter((age) => age < 18).map(String));
  const [showAdults, setShowAdults] = useState(false);
  const [focusedChild, setFocusedChild] = useState<number | null>(null);
  const [invalidFields, setInvalidFields] = useState<Record<string, boolean>>({});
  const heading = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (autoFocus) heading.current?.focus();
  }, [autoFocus]);
  const total = adults.length + children.length;
  const publish = (a: string[], c: string[]) =>
    onRoomChange({
      roomIndex,
      paxes: [
        ...a.map((age) =>
          age !== '' && Number.isInteger(Number(age)) && Number(age) >= 18 && Number(age) <= 100 ? Number(age) : NaN,
        ),
        ...c.map((age) =>
          age !== '' && Number.isInteger(Number(age)) && Number(age) >= 0 && Number(age) <= 17 ? Number(age) : NaN,
        ),
      ],
    });
  const countChange = (count: number, adult: boolean) => {
    const current = adult ? adults : children;
    if (count < (adult ? 1 : 0) || count + (adult ? children.length : adults.length) > 6) return;
    const next = Array.from({ length: count }, (_, i) => current[i] ?? (adult ? '30' : ''));
    if (!adult) setFocusedChild(count > children.length ? count - 1 : null);
    (adult ? setAdults : setChildren)(next);
    publish(adult ? next : adults, adult ? children : next);
  };
  const counter = (adult: boolean) => {
    const count = adult ? adults.length : children.length;
    const label = t(adult ? 'hotels.roomMenu.adults' : 'hotels.roomMenu.minors');
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Box>
          <Typography id={`${id}-${adult ? 'adults' : 'children'}`}>{label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t(adult ? 'hotels.roomMenu.adultRange' : 'hotels.roomMenu.childRange')}
          </Typography>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          role="group"
          aria-labelledby={`${id}-${adult ? 'adults' : 'children'}`}
        >
          <Button
            type="button"
            variant="outlined"
            disabled={count <= (adult ? 1 : 0)}
            aria-label={t('hotels.roomMenu.decrease', { group: label, room: roomIndex + 1 })}
            onClick={() => countChange(count - 1, adult)}
            sx={{ minWidth: 44, width: 44, height: 44 }}
          >
            <Remove fontSize="small" />
          </Button>
          <Typography aria-live="polite" sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
            {count}
          </Typography>
          <Button
            type="button"
            variant="outlined"
            disabled={total >= 6}
            aria-label={t('hotels.roomMenu.increase', { group: label, room: roomIndex + 1 })}
            onClick={() => countChange(count + 1, adult)}
            sx={{ minWidth: 44, width: 44, height: 44 }}
          >
            <Add fontSize="small" />
          </Button>
        </Stack>
      </Stack>
    );
  };
  const ages = (values: string[], adult: boolean) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2, mt: 2 }}>
      {values.map((age, index) => {
        const field = `${adult ? 'a' : 'c'}-${index}`;
        const valid =
          age !== '' &&
          Number.isInteger(Number(age)) &&
          Number(age) >= (adult ? 18 : 0) &&
          Number(age) <= (adult ? 100 : 17);
        return (
          <TextField
            key={field}
            id={`${id}-${field}`}
            label={t(adult ? 'hotels.roomMenu.adultAge' : 'hotels.roomMenu.minorAge', { index: index + 1 })}
            value={age}
            required
            select={!adult}
            SelectProps={adult ? undefined : { native: true }}
            InputLabelProps={{ shrink: true }}
            type={adult ? 'number' : undefined}
            size="small"
            autoFocus={!adult && focusedChild === index}
            inputProps={adult ? { min: 18, max: 100, step: 1, inputMode: 'numeric' } : undefined}
            sx={{ '@media (pointer: coarse)': { '& input, & select': { fontSize: '16px' } } }}
            error={!!invalidFields[field] && !valid}
            helperText={
              invalidFields[field] && !valid
                ? t(adult ? 'hotels.roomMenu.invalidAdultAge' : 'hotels.roomMenu.requiredChildAge')
                : !adult && index === 0
                  ? t('hotels.roomMenu.infantAge')
                  : undefined
            }
            onBlur={() => setInvalidFields((current) => ({ ...current, [field]: true }))}
            onInvalid={() => {
              setInvalidFields((current) => ({ ...current, [field]: true }));
              if (adult) setShowAdults(true);
            }}
            onChange={(event) => {
              const next = values.map((value, i) => (i === index ? event.target.value : value));
              (adult ? setAdults : setChildren)(next);
              publish(adult ? next : adults, adult ? children : next);
            }}
          >
            {!adult && [
              <option key="empty" value="">
                {t('hotels.roomMenu.chooseAge')}
              </option>,
              ...Array.from({ length: 18 }, (_, value) => (
                <option key={value} value={value}>
                  {value === 0 ? t('hotels.roomMenu.lessThanOneYear') : t('hotels.roomMenu.yearsOld', { count: value })}
                </option>
              )),
            ]}
          </TextField>
        );
      })}
    </Box>
  );
  return (
    <Box component="fieldset" sx={{ m: 0, minWidth: 0, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Typography component="legend" variant="subtitle2" sx={{ px: 0.75 }}>
        {t('hotels.roomMenu.room')} {roomIndex + 1}
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography component="span" variant="body2" color="text.secondary" tabIndex={-1} ref={heading}>
          {t('hotels.roomMenu.passengerCount', { count: total })}
        </Typography>
        {canRemove && (
          <Button
            type="button"
            size="small"
            onClick={onRemove}
            aria-label={t('hotels.roomMenu.removeRoomLabel', { room: roomIndex + 1 })}
          >
            {t('hotels.roomMenu.removeRoom')}
          </Button>
        )}
      </Stack>
      <Stack gap={2}>
        {counter(true)}
        {counter(false)}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {t('hotels.roomMenu.roomCapacity')}
      </Typography>
      {children.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {t('hotels.roomMenu.ageAtCheckIn')}
          </Typography>
          {ages(children, false)}
        </Box>
      )}
      <Box
        component="details"
        open={showAdults}
        onInvalidCapture={(event) => {
          event.currentTarget.setAttribute('open', '');
          setShowAdults(true);
        }}
        onToggle={(event) => setShowAdults(event.currentTarget.open)}
        sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Typography component="summary" variant="body2" sx={{ cursor: 'pointer' }}>
          {t('hotels.roomMenu.adultAges')}
          {!showAdults && ` · ${adults.join(', ')} ${t('hotels.roomMenu.years')}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('hotels.roomMenu.defaultAdultAge')}
        </Typography>
        {ages(adults, true)}
      </Box>
    </Box>
  );
};
